import React, { useState, useMemo, useEffect } from 'react';
import { MailCheck, AlertTriangle, Pencil, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SendDraftsDialog = ({ open, onOpenChange, invoices, onSent }) => {
  const [month, setMonth] = useState('');
  const allDrafts = useMemo(
    () => (invoices || []).filter((i) => i.status === 'draft'),
    [invoices]
  );
  const drafts = useMemo(() => {
    if (!month) return allDrafts;
    return allDrafts.filter((i) => ((i.invoice_date || i.created_at || '').slice(0, 7)) === month);
  }, [allDrafts, month]);
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      const init = {};
      drafts.forEach((d) => { init[d.id] = true; });
      setSelected(init);
    }
  }, [open, drafts]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const allChecked = drafts.length > 0 && drafts.every((d) => selected[d.id]);

  const toggleAll = () => {
    const next = {};
    if (!allChecked) drafts.forEach((d) => { next[d.id] = true; });
    setSelected(next);
  };

  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (selectedIds.length === 0) { toast.error('Tick the drafts you want to delete'); return; }
    if (!window.confirm(`Delete ${selectedIds.length} draft invoice(s)? Their delivery orders will be released back to unbilled so you can re-generate.`)) return;
    setDeleting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/invoices/delete-drafts`, { invoice_ids: selectedIds });
      toast.success(res.data.message, { duration: 9000 });
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete drafts');
    } finally {
      setDeleting(false);
    }
  };

  const handleSend = async () => {
    if (selectedIds.length === 0) { toast.error('Tick at least one draft to send'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/invoices/send-batch`, { invoice_ids: selectedIds });
      const ok = res.data.sent?.length || 0;
      const bad = res.data.failed || [];
      if (ok) toast.success(`${ok} invoice(s) emailed to customers`, { duration: 8000 });
      bad.forEach((f) => toast.error(`${f.number || f.id}: ${f.reason}`, { duration: 10000 }));
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send invoices');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="send-drafts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MailCheck size={20} className="text-sky-700" /> Send Draft Invoices
          </DialogTitle>
          <DialogDescription>
            Tick the drafts you've double-checked, then email them all in one go. Each customer gets their invoice PDF; sent drafts become <b>unpaid</b>.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">Filter by month</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-9 w-40"
              data-testid="send-drafts-month-filter"
            />
          </div>
          {month && (
            <Button variant="ghost" size="sm" onClick={() => setMonth('')} data-testid="send-drafts-month-clear">
              Show all months
            </Button>
          )}
          <span className="text-xs text-gray-500 mb-2 ml-auto">
            {drafts.length} draft{drafts.length !== 1 ? 's' : ''}{month ? ` in ${month}` : ' total'}
          </span>
        </div>
        {drafts.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center" data-testid="send-drafts-empty">{month ? `No draft invoices in ${month}.` : 'No draft invoices to send. Use "Monthly Inv → Generate ALL as drafts" first.'}</p>
        ) : (
          <div className="max-h-80 overflow-y-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="p-2 w-10">
                    <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="send-drafts-select-all" />
                  </th>
                  <th className="p-2 text-left">Invoice</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Customer</th>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2 text-center">Review</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => {
                  const hasEmail = (d.customer_email || '').includes('@');
                  return (
                    <tr key={d.id} className="border-t hover:bg-sky-50/50">
                      <td className="p-2 text-center">
                        <Checkbox
                          checked={!!selected[d.id]}
                          onCheckedChange={(v) => setSelected((s) => ({ ...s, [d.id]: !!v }))}
                          data-testid={`send-drafts-check-${d.document_number}`}
                        />
                      </td>
                      <td className="p-2 font-medium text-sky-800 whitespace-nowrap">
                        {d.document_number}
                        {d.is_monthly && <Badge variant="outline" className="ml-1.5 text-[10px] border-emerald-400 text-emerald-700">Monthly</Badge>}
                      </td>
                      <td className="p-2 text-xs text-gray-600 whitespace-nowrap">
                        {(d.invoice_date || d.created_at || '').slice(0, 10) ? new Date(d.invoice_date || d.created_at).toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </td>
                      <td className="p-2">{d.customer_name}</td>
                      <td className="p-2 text-xs text-gray-600">
                        {hasEmail ? d.customer_email : (
                          <span className="flex items-center gap-1 text-amber-600"><AlertTriangle size={13} /> no email</span>
                        )}
                      </td>
                      <td className="p-2 text-right font-semibold whitespace-nowrap">${(d.total_amount || 0).toFixed(2)}</td>
                      <td className="p-2 whitespace-nowrap text-center">
                        <a
                          href={`/admin/billing/invoice/${d.id}/edit`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 border border-emerald-300 rounded px-2 py-1 hover:bg-emerald-50 mr-1"
                          data-testid={`draft-edit-link-${d.document_number}`}
                        >
                          <Pencil size={12} /> Edit
                        </a>
                        <a
                          href={`${BACKEND_URL}/api/invoices/${d.id}/pdf`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 border border-sky-300 rounded px-2 py-1 hover:bg-sky-50"
                          data-testid={`draft-pdf-link-${d.document_number}`}
                        >
                          <FileText size={12} /> PDF
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="outline"
            onClick={handleDelete}
            disabled={deleting || sending || selectedIds.length === 0}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid="send-drafts-delete-btn"
          >
            {deleting ? 'Deleting...' : `Delete ${selectedIds.length} Selected`}
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || deleting || selectedIds.length === 0}
            className="bg-sky-700 hover:bg-sky-800"
            data-testid="send-drafts-send-btn"
          >
            {sending ? 'Sending...' : `Email ${selectedIds.length} Selected`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
